import { useSelector } from 'react-redux'
import props from '../../../../../../redux/props'
import { formatNumber } from '../../../../../../utility'
import { useEffect, useState } from 'react'
import Checkbox from '../../../../../UI/Checkbox/Checkbox'
import useConceptConfiguration from '../../ConceptCalculation/useConceptConfiguration'

const managerUUID = '__manager'
const leaderUUID = '__leader'

const ParticipationRate: React.FC<{}> = () => {
  const { context, getActiveConcept } = useConceptConfiguration()
  const t = useSelector((s) => s[props.TRANSLATION])
  const [selectedUnits, setSelectedUnits] = useState<string[]>(null)

  useEffect(() => {
    if (!selectedUnits) {
      setSelectedUnits([...context.organizationUnitList.map((u) => u.uuid), managerUUID, leaderUUID])
    }
  }, [selectedUnits, context.organizationUnitList])

  const getUnitPeopleCount = (unitUUID: string) => {
    const unit = context.organizationUnitList.find((u) => u.uuid === unitUUID)
    return !!unit ? unit.employeeAmount : 0
  }

  const getTotalUnitPeopleCount = () => {
    return context.organizationUnitList.filter((u) => selectedUnits.includes(u.uuid)).reduce((acc, cur) => acc + getUnitPeopleCount(cur.uuid), 0)
  }

  const getUnitAnswerCount = (unitUUID: string) => {
    const answer = context.answerCountByOrganizationUnit.find((a) => a.organizationUnitUUID === unitUUID)
    return !!answer ? answer.answerCount : 0
  }

  const getManagerAmount = () => {
    return context.organizationUnitList.length
  }

  const getLeaderAmount = () => 1

  const getParticipationRate = (answerCount: number, expected: number): string => {
    if (expected === 0) return '0'
    return formatNumber((100 / expected) * answerCount)
  }

  const getTotalExpected = () => {
    let total = getTotalUnitPeopleCount()
    if (selectedUnits.includes(managerUUID)) {
      total += getManagerAmount()
    }
    if (selectedUnits.includes(leaderUUID)) {
      total += getLeaderAmount()
    }
    return total
  }

  const DefaultEntry: React.FC<{ uuid?: string; name: string; answerCount: number; expected: number; toggle?: () => void; bold?: boolean }> = ({
    uuid,
    name,
    answerCount,
    expected,
    toggle,
    bold = false,
  }) => {
    return (
      <div className="flex gap-2">
        {!!uuid && <Checkbox width="w-fit" checked={selectedUnits.includes(uuid)} onChange={toggle} />}
        <div className={`w-1/3 ${bold && 'font-bold'}`}>{name}</div>
        <div className={`w-1/3 shrink-0 ${bold && 'font-bold'}`}>
          {answerCount}
          {t.survey.analysis.info.participationRate.of}
          {expected}
        </div>
        <div className={`w-1/3 shrink-0 ${bold && 'font-bold'}`}>
          {getParticipationRate(answerCount, expected)}
          {t.generic.percentageSuffix}
        </div>
      </div>
    )
  }

  const getSelectedTotalAnswerCount = () => {
    let selectedTotal = 0
    selectedTotal += context.answerCountByOrganizationUnit.reduce((acc, cur) => {
      if (selectedUnits.includes(cur.organizationUnitUUID)) {
        return acc + cur.answerCount
      }
      return acc
    }, 0)
    if (selectedUnits.includes(managerUUID)) {
      selectedTotal += context.answerCountByManager
    }
    if (selectedUnits.includes(leaderUUID)) {
      selectedTotal += context.answerCountByLeader
    }
    return selectedTotal
  }

  const toggleUnit = (unitUUID: string) => () => {
    if (selectedUnits.includes(unitUUID)) {
      setSelectedUnits(selectedUnits.filter((u) => u !== unitUUID))
    } else {
      setSelectedUnits([...selectedUnits, unitUUID])
    }
  }

  const getUnitsWithoutManagerAnswer = (): string[] => {
    const concept = getActiveConcept()
    if (!concept) return []
    return context.organizationUnitList.reduce((acc, unit) => {
      const conceptUnit = concept.units[unit.uuid]
      if (!conceptUnit || !conceptUnit.managerAnswer) {
        return [...acc, unit.name]
      }
      return acc
    }, [])
  }

  if (!selectedUnits) return null
  return (
    <div>
      <div className="border-b border-blue">{t.survey.analysis.info.participationRate.title}</div>
      <div className="flex flex-col gap-2 mt-2">
        {context.organizationUnitList.map((u) => {
          return (
            <DefaultEntry
              key={u.uuid}
              uuid={u.uuid}
              name={t.survey.analysis.info.participationRate.unitPrefix + u.name}
              answerCount={getUnitAnswerCount(u.uuid)}
              expected={getUnitPeopleCount(u.uuid)}
              toggle={toggleUnit(u.uuid)}
            />
          )
        })}
        <DefaultEntry
          uuid={leaderUUID}
          name={t.survey.analysis.info.participationRate.byLeader}
          answerCount={context.answerCountByLeader}
          expected={getLeaderAmount()}
          toggle={toggleUnit(leaderUUID)}
        />
        <div>
          <DefaultEntry
            uuid={managerUUID}
            name={t.survey.analysis.info.participationRate.byManager}
            answerCount={context.answerCountByManager}
            expected={getManagerAmount()}
            toggle={toggleUnit(managerUUID)}
          />
          {getUnitsWithoutManagerAnswer().length > 0 && (
            <div>
              {t.survey.analysis.info.participationRate.oeWithoutManagerAnswer}
              {getUnitsWithoutManagerAnswer().join(', ')}
            </div>
          )}
        </div>
        <div className="border-b border-lightGray"></div>
        <DefaultEntry
          bold
          name={t.survey.analysis.info.participationRate.total}
          answerCount={getSelectedTotalAnswerCount()}
          expected={getTotalExpected()}
        />
      </div>
    </div>
  )
}

export default ParticipationRate
